<app-loading *ngIf="contentReady"></app-loading>

<div class="bb-page" *ngIf="!contentReady">
  <div class="bb-page-shell">

    <div class="bb-page-header">
      <div class="bb-page-title-block">
        <h2 class="bb-page-title">
          {{ 'New Booking' | transloco }}
        </h2>
      </div>
    </div>

    <div class="bb-card bb-p-20">
      <form [formGroup]="bookingForm" (ngSubmit)="saveBooking()" class="bb-grid bb-gap-16">

        @if(submitted && bookingForm.invalid) {
        <div class="bb-form-alert" role="alert">
          {{ 'Review the booking data before saving' | transloco }}

          @if(getMissingRequiredCount() > 0) {
          <span>
            ({{ getMissingRequiredCount() }} {{ 'required fields' | transloco }})
          </span>
          }
        </div>
        }

        @if(serverError) {
        <div class="bb-form-alert" role="alert">
          {{ serverError }}
        </div>
        }

        <div class="bb-form-grid">
          <div class="bb-field">
            <label class="bb-label" for="pet_service_id">
              {{ 'Service' | transloco }}
            </label>

            <div class="bb-select-wrap">
              <select
                class="bb-select"
                id="pet_service_id"
                formControlName="pet_service_id"
                [ngClass]="{
                  'is-invalid': bookingForm.get('pet_service_id')?.touched && bookingForm.get('pet_service_id')?.invalid,
                  'is-valid': bookingForm.get('pet_service_id')?.touched && bookingForm.get('pet_service_id')?.valid
                }"
              >
                <option value="">-- {{ 'choose' | transloco }} --</option>

                @for(service of services; track service.id) {
                <option [value]="service.id">
                  {{ getServiceName(service) }}
                </option>
                }
              </select>
            </div>

            @if(shouldShowError('pet_service_id')) {
            <div class="bb-field-error">
              {{ getErrorMessage('pet_service_id', ('Service' | transloco)) }}
            </div>
            }
          </div>
        </div>

        <div class="bb-form-grid">
          <div class="bb-field bb-field-full">
            <label class="bb-label" for="booking_date">
              {{ 'Booking Date' | transloco }}
            </label>

            <div class="bb-booking-calendar-field" [dir]="isAr ? 'rtl' : 'ltr'">
              <button
                type="button"
                class="bb-booking-calendar-trigger"
                id="booking_day"
                (click)="toggleBookingCalendar()"
                [ngClass]="{
                  'is-invalid': bookingForm.get('booking_date')?.touched && !isSelectedBookingTimeValid(),
                  'is-valid': bookingDateOnly && isSelectedBookingTimeValid()
                }"
              >
                <span>{{ getBookingDateDisplay() }}</span>
                <i class="fa-solid fa-calendar-days"></i>
              </button>

              @if (showBookingCalendar) {
              <div class="bb-booking-calendar-panel">
                <div class="bb-booking-calendar-head">
                  <button type="button" (click)="previousBookingCalendarMonth()">
                    <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
                  </button>

                  <strong>{{ getBookingCalendarTitle() }}</strong>

                  <button type="button" (click)="nextBookingCalendarMonth()">
                    <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-left' : 'fa-chevron-right'"></i>
                  </button>
                </div>

                <div class="bb-booking-calendar-weekdays">
                  @for (dayName of getBookingCalendarWeekDays(); track dayName) {
                  <span>{{ dayName }}</span>
                  }
                </div>

                <div class="bb-booking-calendar-grid">
                  @for (day of getBookingCalendarDays(); track day.date.getTime()) {
                  <button
                    type="button"
                    class="bb-booking-calendar-day"
                    [class.other-month]="!day.isCurrentMonth"
                    [class.today]="day.isToday"
                    [class.selected]="day.isSelected"
                    [disabled]="day.isDisabled"
                    (click)="selectBookingCalendarDate(day.date)"
                  >
                    {{ day.dayNumber }}
                  </button>
                  }
                </div>
              </div>
              }
            </div>

            <input type="hidden" formControlName="booking_date" />

            <div class="bb-time-field">
              <div class="bb-arabic-time-picker">
                <select
                  [value]="getTimeHour12(bookingTime)"
                  (change)="updateBookingTimePart('hour', $event)">
                  @for (hour of hourOptions; track hour) {
                  <option [value]="hour">{{ getTimeOptionLabel(hour) }}</option>
                  }
                </select>

                <span>:</span>

                <select
                  [value]="getTimeMinute(bookingTime)"
                  (change)="updateBookingTimePart('minute', $event)">
                  @for (minute of minuteOptions; track minute) {
                  <option [value]="minute">{{ getTimeOptionLabel(minute) }}</option>
                  }
                </select>

                <select
                  [value]="getTimePeriod(bookingTime)"
                  (change)="updateBookingTimePart('period', $event)">
                  <option value="am">{{ getPeriodOptionLabel('am') }}</option>
                  <option value="pm">{{ getPeriodOptionLabel('pm') }}</option>
                </select>
              </div>

              <small class="bb-time-preview">
                {{ formatTime(bookingTime) }}
              </small>
            </div>

            @if(scheduleLoading) {
            <div class="bb-field-hint">
              {{ 'Loading clinic working hours...' | transloco }}
            </div>
            }

            @if(scheduleError) {
            <div class="bb-field-error">
              {{ scheduleError }}
            </div>
            }

            @if(shouldShowError('booking_date')) {
            <div class="bb-field-error">
              {{ getErrorMessage('booking_date', ('Booking Date' | transloco)) }}
            </div>
            }
          </div>
        </div>

        @if(bookingDateOnly && getBookingTimeError()) {
        <div class="bb-field-error">
          {{ getBookingTimeError() }}
        </div>
        }

        <div class="bb-form-grid">
          <div class="bb-field">
            <label class="bb-label" for="client_name">
              {{ 'client name' | transloco }}
            </label>

            <input
              type="text"
              class="bb-input"
              id="client_name"
              formControlName="client_name"
              placeholder="{{ 'client name' | transloco }}"
              [ngClass]="{
                'is-invalid': bookingForm.get('client_name')?.touched && bookingForm.get('client_name')?.invalid,
                'is-valid': bookingForm.get('client_name')?.touched && bookingForm.get('client_name')?.valid
              }"
            />

            @if(shouldShowError('client_name')) {
            <div class="bb-field-error">
              {{ getErrorMessage('client_name', ('client name' | transloco)) }}
            </div>
            }
          </div>

          <div class="bb-field">
            <label class="bb-label" for="phone_number">
              {{ 'phone' | transloco }}
            </label>

            <input
              type="tel"
              class="bb-input"
              id="phone_number"
              formControlName="phone_number"
              placeholder="{{ 'phone' | transloco }}"
              [ngClass]="{
                'is-invalid': bookingForm.get('phone_number')?.touched && bookingForm.get('phone_number')?.invalid,
                'is-valid': bookingForm.get('phone_number')?.touched && bookingForm.get('phone_number')?.valid
              }"
            />

            @if(shouldShowError('phone_number')) {
            <div class="bb-field-error">
              {{ getErrorMessage('phone_number', ('phone' | transloco)) }}
            </div>
            }

            @if(clientLookupLoading) {
            <div class="bb-field-hint">
              {{ 'Searching for client...' | transloco }}
            </div>
            }

            @if(clientLookupMessage) {
            <div class="bb-field-hint bb-field-hint-success">
              {{ clientLookupMessage }}
            </div>
            }
          </div>
        </div>

        <div class="bb-form-grid">
          <div class="bb-field">
            <label class="bb-label" for="pet_type">
              {{ 'Pet Type' | transloco }}
            </label>

            <input
              type="text"
              class="bb-input"
              id="pet_type"
              formControlName="pet_type"
              [placeholder]="'Example: Dog / Cat' | transloco"
            />
          </div>

          <div class="bb-field">
            <label class="bb-label" for="status">
              {{ 'Booking Status' | transloco }}
            </label>

            <div class="bb-select-wrap">
              <select class="bb-select" id="status" formControlName="status">
                <option [ngValue]="1">
                  {{ 'Approved' | transloco }}
                </option>
                <option [ngValue]="0">
                  {{ 'Pending' | transloco }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="bb-flex bb-items-center bb-gap-12 bb-wrap">
          <button
            class="bb-btn bb-btn-primary"
            type="submit"
            [disabled]="contentReady"
            [class.is-disabled]="bookingForm.invalid || contentReady"
          >
            {{ 'Submit' | transloco }}
          </button>

          <button
            class="bb-btn bb-btn-secondary"
            type="button"
            [disabled]="contentReady"
            (click)="resetBookingForm()"
          >
            {{ 'Reset' | transloco }}
          </button>
        </div>

      </form>
    </div>

  </div>
</div>